// Every enum in Convenor: the value arrays (used by zod and Drizzle), the TS union
// types, and the labels shown on screen. Database values never change; labels may.

export const ORG_ROLES = ["owner", "admin", "member", "viewer"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];
export const EDITOR_ROLES = ["owner", "admin", "member"] as const satisfies readonly OrgRole[];
export const ADMIN_ROLES = ["owner", "admin"] as const satisfies readonly OrgRole[];
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};
export const ORG_ROLE_HINTS: Record<OrgRole, string> = {
  owner: "Owner: everything an admin can do, and manages owners",
  admin: "Admin: manages the team and settings",
  member: "Member: works on contracts and ToRs",
  viewer: "Viewer: can read everything, change nothing",
};
export const isEditorRole = (role: OrgRole): boolean => role !== "viewer";
export const isAdminRole = (role: OrgRole): boolean => role === "owner" || role === "admin";

export const CONTRACT_KINDS = ["grant", "service_fee_based", "service_global_price"] as const;
export type ContractKind = (typeof CONTRACT_KINDS)[number];
export const CONTRACT_KIND_LABELS: Record<ContractKind, string> = {
  grant: "Grant",
  service_fee_based: "Service contract (fee-based)",
  service_global_price: "Service contract (global price)",
};
/** "How this project works" Q1 (Suite Setup Contract): what kind of funding it is. */
export const FUNDING_TYPES = ["service", "grant", "other"] as const;
export type FundingType = (typeof FUNDING_TYPES)[number];
export const FUNDING_TYPE_LABELS: Record<FundingType, string> = { service: "Service contract", grant: "Grant", other: "Other" };
/** "How this project works" Q4: the format of the reports to the funder. */
export const REPORT_FORMATS = ["eu_annex_vi", "own"] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];
export const REPORT_FORMAT_LABELS: Record<ReportFormat, string> = { eu_annex_vi: "EU narrative report (Annex VI)", own: "Our own template" };

/**
 * Onboarding workspace: a project made by the onboarding is a draft until it
 * is started; nobody outside the onboarding sees it (api core/live-projects.ts).
 * Every project made before (and every other way) is live.
 */
export const PROJECT_LIFECYCLE = ["draft", "live"] as const;
export type ProjectLifecycle = (typeof PROJECT_LIFECYCLE)[number];

export const CONTRACT_STATUSES = ["open", "closed"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const PAYMENT_METHODS = ["fee_based", "lump_sum", "global_price"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  fee_based: "Fee-based",
  lump_sum: "Lump sum",
  global_price: "Global price",
};
/** The sentence printed in section 4 of the document (ARCHITECTURE 7). */
export const PAYMENT_METHOD_SENTENCES: Record<PaymentMethod, string> = {
  fee_based: "Paid on a fee basis: actual days worked and costs incurred, at the unit rates above.",
  lump_sum: "Paid as a lump sum on completion of the activity and delivery of the agreed outputs.",
  global_price: "Paid as part of the contract's global price, against the agreed deliverables.",
};

export const TOR_STATUSES = ["draft", "signed_off", "submitted", "revision", "approved", "withdrawn"] as const;
export type TorStatus = (typeof TOR_STATUSES)[number];
export const TOR_STATUS_LABELS: Record<TorStatus, string> = {
  draft: "Draft",
  signed_off: "Signed off",
  submitted: "With the funder",
  revision: "Changes requested",
  approved: "Approved",
  withdrawn: "Withdrawn",
};

export const TOR_ACTIONS = ["sign_off", "reopen", "submit", "approve", "request_revision", "revise", "withdraw"] as const;
export type TorAction = (typeof TOR_ACTIONS)[number];

export const TOR_CHANGE_ACTIONS = [
  "created",
  "signed_off",
  "reopened",
  "submitted",
  "revision_requested",
  "revised",
  "approved",
  "withdrawn",
  "exported",
  "commented",
] as const;
export type TorChangeAction = (typeof TOR_CHANGE_ACTIONS)[number];

/**
 * BU-N12 (Marius, retest 30 Oct): what kind of budget line it is, for the
 * contract's rules on moving money. In EU service contracts, transfers into fee
 * lines and using the contingency or expenditure-verification lines need the
 * funder's prior approval. Set by the budget import where the name or heading
 * says so (guessBudgetLineKind), else "other"; editable on the Budget tab.
 */
export const BUDGET_LINE_KINDS = ["fees", "contingency", "verification", "other"] as const;
export type BudgetLineKind = (typeof BUDGET_LINE_KINDS)[number];
export const BUDGET_LINE_KIND_LABELS: Record<BudgetLineKind, string> = {
  fees: "Fees",
  contingency: "Contingency",
  verification: "Expenditure verification",
  other: "Other",
};
/**
 * Analytics A1/A16 (Marius's wireframe, 30 Oct): the kind of an event, for the
 * Analytics filter and "What an event costs". Set from the event's name where
 * it says so (guessEventKind), else "other"; editable on Edit event.
 */
export const EVENT_KINDS = ["workshop", "training", "conference", "meeting", "study_visit", "other"] as const;
export type EventKind = (typeof EVENT_KINDS)[number];
export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  workshop: "Workshop",
  training: "Training",
  conference: "Conference",
  meeting: "Meeting",
  study_visit: "Study visit",
  other: "Other",
};
/** The kind an event's name makes clear ("Regional workshop on…" is a workshop), else "other". The migration uses the same words. */
export function guessEventKind(title: string): EventKind {
  const t = title.toLowerCase();
  if (/study (visit|tour)/.test(t)) return "study_visit";
  if (/\bworkshops?\b/.test(t)) return "workshop";
  if (/\b(trainings?|course|training of trainers)\b/.test(t)) return "training";
  if (/\b(conferences?|forum|summit|symposium)\b/.test(t)) return "conference";
  if (/\b(meetings?|roundtable|round table|dialogue|consultation)\b/.test(t)) return "meeting";
  return "other";
}

/**
 * The kind of a budget line from its name and heading, only where the EU
 * template's words make it clear: "Key experts" / "Non-key experts" / "Staff" /
 * "Personnel" (fees), "Contingency" / "Reserve" / "Unforeseen", "Expenditure
 * verification". The word "fees" alone is not enough ("Registration fees",
 * "Bank fees", "Venue hire fees" are other costs). Travel, per diems and
 * missions of experts are not fees. Anything else is "other". Migration 0105
 * reads existing lines the same way.
 */
export function guessBudgetLineKind(name: string, heading?: string | null): BudgetLineKind {
  const text = `${heading ?? ""} ${name}`;
  if (/\bcontingenc|\breserve\b|\bunforeseen\b/i.test(name) || /\bcontingenc/i.test(heading ?? "")) return "contingency";
  if (/expenditure verification|verification of expenditure/i.test(text)) return "verification";
  if (/travel|per.?diem|subsistence|accommodation|mission|flight|ticket/i.test(name)) return "other";
  if (/\bexperts?\b|\bstaff\b|\bpersonnel\b|\bnke\b|non.?key/i.test(text)) return "fees";
  return "other";
}

export const COST_LINE_KINDS = [
  "expert_days",
  "per_diem",
  "travel",
  "venue",
  "catering",
  "interpretation",
  "materials",
  "other",
] as const;
export type CostLineKind = (typeof COST_LINE_KINDS)[number];
export const COST_LINE_KIND_LABELS: Record<CostLineKind, string> = {
  expert_days: "Expert days",
  per_diem: "Per diem",
  travel: "Travel",
  venue: "Venue",
  catering: "Catering",
  interpretation: "Interpretation",
  materials: "Materials",
  other: "Other",
};

export const EXPERT_CATEGORIES = ["key_expert", "non_key_senior", "non_key_junior"] as const;
export type ExpertCategory = (typeof EXPERT_CATEGORIES)[number];
export const EXPERT_CATEGORY_LABELS: Record<ExpertCategory, string> = {
  key_expert: "Key expert",
  non_key_senior: "Senior non-key expert",
  non_key_junior: "Junior non-key expert",
};

export const AUDIT_ACTIONS = [
  "contract.created",
  "contract.updated",
  "contract.closed",
  "contract.reopened",
  "tor.created",
  "tor.deleted",
  "tor.signed_off",
  "tor.reopened",
  "tor.submitted",
  "tor.approved",
  "tor.revision_requested",
  "tor.revised",
  "tor.withdrawn",
  "tor.exported",
  "expert.added",
  "expert.updated",
  "expert.removed",
  "member.invited",
  "member.joined",
  "member.role_changed",
  "member.removed",
  "member.left",
  "invitation.revoked",
  "org.updated",
  "org.created",
  // v2 (ARCHITECTURE 15.4)
  "expert_pool.added",
  "expert_pool.updated",
  "expert_pool.removed",
  "timesheet.approved",
  "timesheet.reopened",
  "work.run_days_logged",
  "tor.record_updated",
  "event.created",
  "event.updated",
  "event.deleted",
  "event.forms_changed",
  "event.invitations_sent",
  "registration.removed",
  "contact.added",
  "contact.updated",
  "contact.removed",
  "stakeholder_org.added",
  "stakeholder_org.updated",
  "stakeholder_org.removed",
  "deliverable.added",
  "deliverable.updated",
  "deliverable.removed",
  "logframe.updated",
  "indicator.reading_added",
  "indicator.reading_removed",
  "report.created",
  "report.finalised",
  "report.reopened",
  "report.deleted",
  "report.exported",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_ENTITY_TYPES = [
  "contract",
  "tor",
  "expert",
  "member",
  "invitation",
  "org",
  // v2
  "pool_expert",
  "timesheet",
  "event",
  "registration",
  "contact",
  "stakeholder_org",
  "deliverable",
  "logframe",
  "indicator",
  "report",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

export const ATTENTION_KINDS = [
  "ready_to_send",
  "waiting_long",
  "revision_requested",
  "my_draft",
  // v2 (API.md 6.2): produced by the dashboard once the activity record exists
  "debrief_missing",
] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];

// ---------------------------------------------------------------------------
// v2: the suite (ARCHITECTURE 15.1). Value arrays feed zod and pgEnum.
// ---------------------------------------------------------------------------

/** v3 adds assignment, event, mission, meeting and import (lines "from the record"). */
export const WORK_SOURCES = ["manual", "run_day", "assignment", "event", "mission", "meeting", "import"] as const;
export type WorkSource = (typeof WORK_SOURCES)[number];
export const WORK_SOURCE_LABELS: Record<WorkSource, string> = {
  manual: "Entered by hand",
  run_day: "Run-day",
  assignment: "From an assignment",
  event: "From the event record",
  mission: "From the mission",
  meeting: "From a meeting",
  import: "Imported",
};

/** v3 adds waitlisted and maybe (SPEC-v3 4.2). */
export const REGISTRATION_STATUSES = ["invited", "registered", "declined", "cancelled", "waitlisted", "maybe"] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];
export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, string> = {
  invited: "Invited",
  registered: "Registered",
  declined: "Declined",
  cancelled: "Cancelled",
  waitlisted: "Waiting list",
  maybe: "Maybe",
};

export const REGISTRATION_SOURCES = ["invited", "form", "staff", "walk_in"] as const;
export type RegistrationSource = (typeof REGISTRATION_SOURCES)[number];
export const REGISTRATION_SOURCE_LABELS: Record<RegistrationSource, string> = {
  invited: "Invited",
  form: "Registration form",
  staff: "Added by staff",
  walk_in: "Walk-in",
};

/** Stored gender values, in picker order. "other" = the person describes themselves another way. */
export const GENDERS = ["female", "male", "non_binary", "other", "undisclosed"] as const;
export type Gender = (typeof GENDERS)[number];
export const GENDER_LABELS: Record<Gender, string> = {
  female: "Woman",
  male: "Man",
  non_binary: "Non-binary",
  other: "I describe myself another way",
  undisclosed: "I prefer not to say",
};

/**
 * The gender answers the v3 screens and APIs send, in picker order. They map
 * one to one onto GENDERS (woman = female, man = male, prefer_not = undisclosed).
 */
export const GENDER_ANSWERS = ["woman", "man", "non_binary", "other", "prefer_not"] as const;
export type GenderAnswer = (typeof GENDER_ANSWERS)[number];
export const GENDER_ANSWER_LABELS: Record<GenderAnswer, string> = {
  woman: GENDER_LABELS.female,
  man: GENDER_LABELS.male,
  non_binary: GENDER_LABELS.non_binary,
  other: GENDER_LABELS.other,
  prefer_not: GENDER_LABELS.undisclosed,
};
const GENDER_ANSWER_TO_DB: Record<GenderAnswer, Gender> = { woman: "female", man: "male", non_binary: "non_binary", other: "other", prefer_not: "undisclosed" };
const GENDER_DB_TO_ANSWER: Record<Gender, GenderAnswer> = { female: "woman", male: "man", non_binary: "non_binary", other: "other", undisclosed: "prefer_not" };
/** A v3 gender answer as the stored value (null stays null). */
export const genderAnswerToDb = (g: GenderAnswer | null | undefined): Gender | null => (g ? GENDER_ANSWER_TO_DB[g] : null);
/** A stored gender as the v3 answer (null stays null). */
export const genderAnswerFromDb = (g: Gender | null | undefined): GenderAnswer | null => (g ? GENDER_DB_TO_ANSWER[g] : null);

export const STAKEHOLDER_TYPES = [
  "government",
  "civil_society",
  "academia",
  "private_sector",
  "international_org",
  "media",
  "other",
] as const;
export type StakeholderType = (typeof STAKEHOLDER_TYPES)[number];
export const STAKEHOLDER_TYPE_LABELS: Record<StakeholderType, string> = {
  government: "Government",
  civil_society: "Civil society",
  academia: "Academia and research",
  private_sector: "Private sector",
  international_org: "International organisation",
  media: "Media",
  other: "Other",
};

/** v3 adds import (SPEC-v3 4.2). */
export const CONTACT_SOURCES = ["manual", "event", "import"] as const;
export type ContactSource = (typeof CONTACT_SOURCES)[number];
export const CONTACT_SOURCE_LABELS: Record<ContactSource, string> = {
  manual: "Added by hand",
  event: "From an event",
  import: "Imported",
};

export const CONTRIBUTION_ROLES = ["speaker", "facilitator", "author", "reviewer", "partner", "host", "other"] as const;
export type ContributionRole = (typeof CONTRIBUTION_ROLES)[number];
export const CONTRIBUTION_ROLE_LABELS: Record<ContributionRole, string> = {
  speaker: "Speaker",
  facilitator: "Facilitator",
  author: "Author",
  reviewer: "Reviewer",
  partner: "Partner",
  host: "Host",
  other: "Other",
};

export const DELIVERABLE_KINDS = ["study", "report", "publication", "policy_brief", "training_material", "other"] as const;
export type DeliverableKind = (typeof DELIVERABLE_KINDS)[number];
export const DELIVERABLE_KIND_LABELS: Record<DeliverableKind, string> = {
  study: "Study",
  report: "Report",
  publication: "Publication",
  policy_brief: "Policy brief",
  training_material: "Training material",
  other: "Other",
};

/**
 * v3 workflow (SPEC-v3 6.2): planned -> drafting -> in_review -> approved -> published.
 * Migration 0002 maps v2 values: in_progress -> drafting, delivered -> approved.
 */
export const DELIVERABLE_STATUSES = ["planned", "drafting", "in_review", "approved", "published"] as const;
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];
export const DELIVERABLE_STATUS_LABELS: Record<DeliverableStatus, string> = {
  planned: "Planned",
  drafting: "Drafting",
  in_review: "In review",
  approved: "Approved",
  published: "Published",
};
/** Statuses that count as "delivered" in v2 terms (progress bars, indicators, analytics). */
export const DELIVERED_STATUSES = ["approved", "published"] as const satisfies readonly DeliverableStatus[];
export const isDelivered = (s: DeliverableStatus): boolean => s === "approved" || s === "published";

/**
 * The old fixed levels. Since R1 a result stores a level number (1 = top) and
 * the project names its 2 to 5 levels; these three stay only as the stored
 * value of levels 1 to 3 (logframe_result.level, kept in sync by the database).
 */
export const RESULT_LEVELS = ["impact", "outcome", "output"] as const;
export type ResultLevel = (typeof RESULT_LEVELS)[number];
export const RESULT_LEVEL_LABELS: Record<ResultLevel, string> = {
  impact: "Impact",
  outcome: "Outcome",
  output: "Output",
};
/** The level names of a new project, and of every project before R1. */
export const DEFAULT_RESULT_LEVEL_NAMES = ["Impact", "Outcome", "Output"] as const;
export const RESULT_LEVELS_MIN = 2;
export const RESULT_LEVELS_MAX = 5;
/** impact → 1, outcome → 2, output → 3. */
export const levelNoOf = (level: ResultLevel): number => RESULT_LEVELS.indexOf(level) + 1;
/** 1 → impact, 2 → outcome, 3 → output; 4 and 5 have no old value. */
export const legacyLevelOf = (levelNo: number): ResultLevel | null => RESULT_LEVELS[levelNo - 1] ?? null;
/** The project's name of a level: "Specific objective"; "Level 4" when the list is shorter. */
export const resultLevelName = (names: readonly string[], levelNo: number): string => names[levelNo - 1]?.trim() || `Level ${levelNo}`;

export const INDICATOR_AUTO_SOURCES = [
  "participants_attended",
  "participants_women_pct",
  "events_held",
  "deliverables_delivered",
  "expert_days_logged",
  "organisations_reached",
  // v3: share of usefulness answers 4 or 5 on the linked event's feedback (SPEC-v3 6.3)
  "satisfaction_pct",
] as const;
export type IndicatorAutoSource = (typeof INDICATOR_AUTO_SOURCES)[number];
export const INDICATOR_AUTO_SOURCE_LABELS: Record<IndicatorAutoSource, string> = {
  participants_attended: "Participants attended",
  participants_women_pct: "Share of women among participants (%)",
  events_held: "Events held",
  deliverables_delivered: "Deliverables delivered",
  expert_days_logged: "Expert days logged",
  organisations_reached: "Organisations reached",
  satisfaction_pct: "Share of participants who found the event useful (%)",
};

/**
 * R3: the three ways an indicator measures. A count comes from our own
 * records (an automatic source, or counted by hand); a share comes from
 * feedback answers (questions linked to the indicator); a reading of the
 * outside world is typed in with its proof.
 */
export const INDICATOR_KINDS = ["count", "share_from_answers", "reading"] as const;
export type IndicatorKind = (typeof INDICATOR_KINDS)[number];
export const INDICATOR_KIND_LABELS: Record<IndicatorKind, string> = {
  count: "Count from our records",
  share_from_answers: "Share from feedback answers",
  reading: "Reading of the outside world",
};
export const INDICATOR_KIND_HINTS: Record<IndicatorKind, string> = {
  count: "Things the project records itself: events, participants, publications, expert days.",
  share_from_answers: "A share of people's answers to a feedback question, for example “% who say their view changed”.",
  reading: "A change outside the project, such as a law passed or a process influenced. Someone types it in, with proof.",
};
/** The automatic sources of a count (satisfaction_pct is a share from answers). */
export const COUNT_AUTO_SOURCES = INDICATOR_AUTO_SOURCES.filter((s) => s !== "satisfaction_pct");
/** The kind an automatic source implies: none → reading; satisfaction → share from answers; else count. */
export const indicatorKindForAutoSource = (s: IndicatorAutoSource | null): IndicatorKind =>
  s === null ? "reading" : s === "satisfaction_pct" ? "share_from_answers" : "count";
/** Whether an automatic source fits a kind (none always fits). */
export const autoSourceFitsKind = (kind: IndicatorKind, s: IndicatorAutoSource | null): boolean =>
  s === null || indicatorKindForAutoSource(s) === kind;

/** How an indicator reading was obtained (round 4, R5). null on a reading = not recorded. */
export const READING_METHODS = ["counted_records", "from_answers", "sample_estimate", "observed", "outside_source"] as const;
export type ReadingMethod = (typeof READING_METHODS)[number];
export const READING_METHOD_LABELS: Record<ReadingMethod, string> = {
  counted_records: "Counted from our records",
  from_answers: "From feedback answers",
  sample_estimate: "Estimated from a sample",
  observed: "Observed directly",
  outside_source: "Outside source",
};
/** The method of a reading written from an automatic source: feedback answers, else counted from the record. */
export const readingMethodForAutoSource = (s: IndicatorAutoSource | null): ReadingMethod | null =>
  s === null ? null : s === "satisfaction_pct" ? "from_answers" : "counted_records";
/** The method of a reading proposed for an indicator of this kind: none for a reading of the outside world. */
export const readingMethodForKind = (kind: IndicatorKind): ReadingMethod | null =>
  kind === "share_from_answers" ? "from_answers" : kind === "count" ? "counted_records" : null;
/** "Outside source: Eurostat", "Counted from our records"; null when the method was not recorded. */
export function readingMethodText(method: ReadingMethod | null, sourceName: string | null): string | null {
  if (!method) return null;
  return method === "outside_source" && sourceName ? `${READING_METHOD_LABELS[method]}: ${sourceName}` : READING_METHOD_LABELS[method];
}

/**
 * v3 (SPEC-v3 4.2, 6.7). Migration 0002 maps v2 values: draft -> draft, final -> submitted.
 */
export const REPORT_STATUSES = ["draft", "submitted", "approved", "approved_with_comments", "changes_requested"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];
/** The same words as the report chips on Reports and Approvals (the funder accepts a report, with or without questions, or does not). */
export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  draft: "Draft",
  submitted: "With the funder",
  approved: "Accepted",
  approved_with_comments: "Accepted with questions",
  changes_requested: "Not accepted",
};

/** Registration form set-up (the "RSVP builder"): each standard field is off, optional or required. */
export const FIELD_MODES = ["off", "optional", "required"] as const;
export type FieldMode = (typeof FIELD_MODES)[number];
export const FIELD_MODE_LABELS: Record<FieldMode, string> = {
  off: "Off",
  optional: "Optional",
  required: "Required",
};

export const QUESTION_KINDS = ["text", "yes_no", "choice"] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];
export const QUESTION_KIND_LABELS: Record<QuestionKind, string> = {
  text: "Short text",
  yes_no: "Yes or no",
  choice: "One choice from a list",
};

export const PUBLIC_FORM_KINDS = ["registration", "check_in", "feedback"] as const;
export type PublicFormKind = (typeof PUBLIC_FORM_KINDS)[number];
export const PUBLIC_FORM_KIND_LABELS: Record<PublicFormKind, string> = {
  registration: "Registration form",
  check_in: "Check-in form",
  feedback: "Feedback form",
};

/** The activity checklist on an approved ToR (SPEC-v2 W1, ARCHITECTURE 19.4). */
export const CHECKLIST_KEYS = ["approved", "experts_assigned", "work_logged", "check_in", "debrief"] as const;
export type ChecklistKey = (typeof CHECKLIST_KEYS)[number];
export const CHECKLIST_LABELS: Record<ChecklistKey, string> = {
  approved: "ToR approved",
  experts_assigned: "Experts assigned",
  work_logged: "Work logged",
  check_in: "Check-in complete",
  debrief: "Debrief written",
};

export const GENERATED_DOCUMENT_KINDS = ["report", "logframe", "timesheet", "attendance"] as const;
export type GeneratedDocumentKind = (typeof GENERATED_DOCUMENT_KINDS)[number];

export const ATTENDANCE_MODES = ["attended", "sign_in"] as const;
export type AttendanceMode = (typeof ATTENDANCE_MODES)[number];

export const RUN_DAY_SKIP_REASONS = ["already_logged", "month_approved", "over_24_hours"] as const;
export type RunDaySkipReason = (typeof RUN_DAY_SKIP_REASONS)[number];

export const INVITE_SKIP_REASONS = ["no_email", "not_contactable", "already_listed"] as const;
export type InviteSkipReason = (typeof INVITE_SKIP_REASONS)[number];

// ===========================================================================
// v3 (ARCHITECTURE-v3 3.8). Value arrays feed zod and pgEnum; labels are what
// the screens show. Database values never change; labels may.
// ===========================================================================

type Labels<T extends readonly string[]> = Record<T[number], string>;

export const USER_KINDS = ["staff", "expert"] as const;
export type UserKind = (typeof USER_KINDS)[number];
export const USER_KIND_LABELS: Labels<typeof USER_KINDS> = { staff: "Team member", expert: "Expert" };

export const SIGNUP_INTENTS = ["setup_org", "invited", "partner", "expert"] as const;
export type SignupIntent = (typeof SIGNUP_INTENTS)[number];
export const SIGNUP_INTENT_LABELS: Labels<typeof SIGNUP_INTENTS> = {
  setup_org: "Set up my organisation",
  invited: "I was invited by a colleague",
  partner: "I am a consortium partner",
  expert: "I am an expert",
};

/** Who is making the request (SPEC-v3 2). Funders and guests use tokens, never a session. */
export const PRINCIPALS = ["staff", "expert", "partner"] as const;
export type Principal = (typeof PRINCIPALS)[number];
/** Every kind of caller the tenancy layer knows, including the token principals. */
export const ALL_PRINCIPALS = ["staff", "expert", "partner", "funder", "auditor", "guest", "room", "system"] as const;
export type AnyPrincipal = (typeof ALL_PRINCIPALS)[number];

export const MFA_STATES = ["none", "pending", "enrol", "passed"] as const;
export type MfaState = (typeof MFA_STATES)[number];

export const PROJECT_ROLES = ["member", "viewer"] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];
export const PROJECT_ROLE_LABELS: Labels<typeof PROJECT_ROLES> = { member: "Member", viewer: "Viewer" };
/** "Your role" on a project, as shown on Projects and in Me.project.role. */
export const PROJECT_ACCESS_ROLES = ["owner", "admin", "lead", "member", "viewer", "partner"] as const;
export type ProjectAccessRole = (typeof PROJECT_ACCESS_ROLES)[number];
export const PROJECT_ACCESS_ROLE_LABELS: Labels<typeof PROJECT_ACCESS_ROLES> = {
  owner: "Owner",
  admin: "Admin",
  lead: "Contract lead",
  member: "Member",
  viewer: "Viewer",
  partner: "Consortium partner",
};

export const ORG_TYPES = ["think_tank", "civil_society", "consultancy", "public_body"] as const;
export type OrgType = (typeof ORG_TYPES)[number];
export const ORG_TYPE_LABELS: Labels<typeof ORG_TYPES> = {
  think_tank: "Think tank or research institute",
  civil_society: "Civil society organisation",
  consultancy: "Consultancy",
  public_body: "Public body",
};

export const WORK_MODES = ["lead", "partner", "both"] as const;
export type WorkMode = (typeof WORK_MODES)[number];
export const WORK_MODE_LABELS: Labels<typeof WORK_MODES> = {
  lead: "We lead projects",
  partner: "We are a partner in projects",
  both: "Both",
};

export const RETENTION_POLICIES = ["project_plus_7", "years_5", "years_3"] as const;
export type RetentionPolicy = (typeof RETENTION_POLICIES)[number];
export const RETENTION_POLICY_LABELS: Labels<typeof RETENTION_POLICIES> = {
  project_plus_7: "Project period + 7 years",
  years_5: "Project period + 5 years",
  years_3: "Project period + 3 years",
};
/** Years kept after the project's retention date (closed), its end date (not closed), or the person's last activity (no end date, or no project). */
export const RETENTION_POLICY_YEARS: Record<RetentionPolicy, number> = { project_plus_7: 7, years_5: 5, years_3: 3 };

export const RETENTION_CHOICES = ["keep_retention", "move_to_org", "delete_personal"] as const;
export type RetentionChoice = (typeof RETENTION_CHOICES)[number];

export const HISTORY_MODES = ["readonly", "editable_30"] as const;
export type HistoryMode = (typeof HISTORY_MODES)[number];

export const SETUP_STEPS = ["contract", "logframe", "team", "torTemplate", "branding"] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];
/**
 * What a person does on one project (Home per person): it decides which
 * "Needs your attention" items are theirs. The contract lead is not a
 * function: it is project.lead_user_id.
 */
export const PROJECT_FUNCTIONS = ["director", "coordinator", "events", "finance", "monitoring"] as const;
export type ProjectFunction = (typeof PROJECT_FUNCTIONS)[number];
export const PROJECT_FUNCTION_LABELS: Labels<typeof PROJECT_FUNCTIONS> = {
  director: "Project director",
  coordinator: "Coordinator",
  events: "Event organiser",
  finance: "Finance and admin",
  monitoring: "Monitoring and evaluation",
};
/**
 * Who acts on a "Needs your attention" item when no one person owns it:
 * the lead (the contract lead and the project director), finance, the event
 * organiser, monitoring and evaluation, or the whole team. When nobody on the
 * project has that function, the lead acts. "admins": only the organisation's
 * owners and admins can act (Data and privacy, ST-N2); nobody else sees it.
 */
export const ATTENTION_AUDIENCES = ["lead", "finance", "events", "monitoring", "team", "admins"] as const;
export type AttentionAudience = (typeof ATTENTION_AUDIENCES)[number];
export const ONBOARDING_STEPS = ["account", "organisation", "team", "houseStyle", "library"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];
export const STEP_STATES = ["todo", "done", "skipped"] as const;
export type StepState = (typeof STEP_STATES)[number];

/** What an upload is for (API-v3 4). Decides the allowed types and the size limit. */
export const FILE_PURPOSES = [
  "logo",
  "banner",
  "partner_logo",
  "cv",
  "tor_template",
  "contract_file",
  "logframe",
  "contacts_sheet",
  "evidence",
  "attachment",
  "receipt",
  "invoice_pdf",
  "proof",
  "funder_letter",
  "identity_proof",
  "document",
  "deliverable_version",
  "sc_paper",
  /** A speaker's photo on their stakeholder record. */
  "photo",
  /** A speaker's slides on their guest row. */
  "slides",
  /** A budget (csv/xlsx) for the budget import on Administration. */
  "budget_sheet",
] as const;
export type FilePurpose = (typeof FILE_PURPOSES)[number];
/** stored_file.kind: every upload purpose, plus chunked project imports and files the app generates. */
export const FILE_KINDS = [...FILE_PURPOSES, "project_import", "generated"] as const;
export type FileKind = (typeof FILE_KINDS)[number];
/** "cm": the funder's contract manager (modules/cm). */
export const FILE_UPLOADED_VIA = ["staff", "expert", "partner", "guest", "system", "cm"] as const;
export type FileUploadedVia = (typeof FILE_UPLOADED_VIA)[number];
export const SCAN_STATUSES = ["clean", "pending", "infected", "skipped"] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];

export const DOCUMENT_TYPES = ["contract", "amendment", "tor", "evidence", "correspondence", "other"] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];
export const DOCUMENT_TYPE_LABELS: Labels<typeof DOCUMENT_TYPES> = {
  contract: "Contract",
  amendment: "Amendment",
  tor: "Terms of Reference",
  evidence: "Evidence",
  correspondence: "Correspondence",
  other: "Other",
};

export const NOTIFICATION_KINDS = [
  "tor_decision",
  "tor_resubmitted",
  "tor_signed_off",
  "timesheet_signed",
  "timesheet_question",
  "timesheet_ready",
  "document_to_sign",
  "declaration_reminder",
  "invoice_submitted",
  "claim_submitted",
  "claim_decided",
  "invoice_paid",
  "report_due",
  "deliverable_uploaded",
  "deliverable_changes_requested",
  "mention",
  "join_request",
  "data_request_due",
  "expert_message",
  "iban_changed",
  "import_finished",
  "digest_daily",
  "ai_budget_reached",
  "funder_document",
  "speaker_details_received",
  "attention_new",
  "guest_reply",
  "digest_weekly",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
export const NOTIFICATION_KIND_LABELS: Labels<typeof NOTIFICATION_KINDS> = {
  tor_decision: "The funder decides on a ToR",
  tor_resubmitted: "A ToR is sent to the funder again",
  tor_signed_off: "A ToR is signed off",
  timesheet_signed: "An expert signs a timesheet",
  timesheet_question: "An expert sends a timesheet back with a question",
  timesheet_ready: "A timesheet is ready to sign",
  document_to_sign: "A document is ready to sign",
  declaration_reminder: "Reminder to sign a declaration",
  invoice_submitted: "An expert sends an invoice",
  claim_submitted: "A claim or cost is sent for approval",
  claim_decided: "A claim is decided",
  invoice_paid: "An invoice is paid",
  report_due: "A report is due",
  deliverable_uploaded: "A deliverable draft is uploaded",
  deliverable_changes_requested: "Changes are requested on a deliverable",
  mention: "Someone mentions you",
  join_request: "Someone asks to join the organisation",
  data_request_due: "A data request is due",
  expert_message: "A message from an expert or the team",
  iban_changed: "An expert changes their bank details",
  import_finished: "An import is finished",
  digest_daily: "Daily summary",
  ai_budget_reached: "The monthly AI limit is reached",
  funder_document: "The funder's contract manager shares a document or a comment",
  speaker_details_received: "A speaker sends their details for an event",
  attention_new: "Something new needs my attention (as on Home)",
  guest_reply: "Guests: invitations sent, answers and feedback",
  digest_weekly: "Weekly summary: what needs me this week (Monday morning)",
};
/** Default channels per kind (SPEC-v3 6.12). */
export const NOTIFICATION_DEFAULTS: Record<NotificationKind, { inApp: boolean; email: boolean }> = {
  tor_decision: { inApp: true, email: true },
  tor_resubmitted: { inApp: true, email: true },
  tor_signed_off: { inApp: true, email: true },
  timesheet_signed: { inApp: true, email: true },
  timesheet_question: { inApp: true, email: true },
  timesheet_ready: { inApp: false, email: true },
  document_to_sign: { inApp: false, email: true },
  declaration_reminder: { inApp: false, email: true },
  invoice_submitted: { inApp: true, email: false },
  claim_submitted: { inApp: true, email: false },
  // GL3 (retest 30 Oct): a cost sent back reaches the bell too.
  claim_decided: { inApp: true, email: true },
  invoice_paid: { inApp: false, email: true },
  report_due: { inApp: true, email: true },
  deliverable_uploaded: { inApp: true, email: true },
  deliverable_changes_requested: { inApp: true, email: true },
  mention: { inApp: true, email: true },
  join_request: { inApp: true, email: true },
  data_request_due: { inApp: true, email: true },
  expert_message: { inApp: true, email: true },
  iban_changed: { inApp: true, email: false },
  import_finished: { inApp: true, email: false },
  digest_daily: { inApp: false, email: false },
  ai_budget_reached: { inApp: true, email: false },
  funder_document: { inApp: true, email: true },
  speaker_details_received: { inApp: true, email: false },
  // GL3: the alert for a new "Needs your attention" item; by email only when someone turns it on.
  attention_new: { inApp: true, email: false },
  // GL3: RSVPs, speakers' answers and feedback; grouped per event while unread (events/lib/guest-notify.ts).
  guest_reply: { inApp: true, email: false },
  // M1 (W11): the Monday summary of what needs the person; an email only, on unless turned off.
  digest_weekly: { inApp: false, email: true },
};

export const AUDIT_MODULES = ["activities", "experts", "stakeholders", "admin", "events", "settings", "projects", "system"] as const;
export type AuditModule = (typeof AUDIT_MODULES)[number];
export const AUDIT_MODULE_LABELS: Labels<typeof AUDIT_MODULES> = {
  activities: "Activities",
  experts: "Experts",
  stakeholders: "Stakeholders",
  admin: "Administration",
  events: "Events",
  settings: "Settings",
  projects: "Projects",
  system: "System",
};

export const IMPORT_PILES = ["contract", "logframe", "tors", "reports", "timesheets", "attendance", "cvs", "contacts", "evidence"] as const;
export type ImportPile = (typeof IMPORT_PILES)[number];
export const IMPORT_PILE_LABELS: Labels<typeof IMPORT_PILES> = {
  contract: "Contract and addenda",
  logframe: "Logframe",
  tors: "Terms of Reference",
  reports: "Reports",
  timesheets: "Timesheets",
  attendance: "Attendance lists",
  cvs: "CVs",
  contacts: "Contacts",
  evidence: "Evidence and other files",
};
export const IMPORT_JOB_STATUSES = ["uploading", "sorting", "review", "confirmed", "committed", "failed"] as const;
export type ImportJobStatus = (typeof IMPORT_JOB_STATUSES)[number];

export const ACTIVITY_TYPES = ["event", "deliverable", "nke"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];
export const ACTIVITY_TYPE_LABELS: Labels<typeof ACTIVITY_TYPES> = {
  event: "Event",
  deliverable: "Deliverable",
  // X-11 (audit 3 Nov): plain words for a new user; key and non-key experts (KE, NKE) do the work.
  nke: "Expert assignment",
};
/** "Events", "Deliverables": a line of work's kind in names and headings (W1). */
export const ACTIVITY_TYPE_PLURAL_LABELS: Labels<typeof ACTIVITY_TYPES> = {
  event: "Events",
  deliverable: "Deliverables",
  nke: "Expert assignments",
};

export const ACTIVITY_STATUSES = [
  "planned",
  /** D6: the funder's approval is switched off for it; nothing waits for a ToR. */
  "no_approval_needed",
  "tor_with_funder",
  "tor_approved",
  "in_progress",
  "in_review",
  "changes_requested",
  "done",
] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];
export const ACTIVITY_STATUS_LABELS: Labels<typeof ACTIVITY_STATUSES> = {
  planned: "Planned",
  no_approval_needed: "No approval needed",
  tor_with_funder: "ToR with the funder",
  tor_approved: "ToR approved",
  in_progress: "In progress",
  in_review: "In review",
  changes_requested: "Changes requested",
  done: "Done",
};

export const MEETING_KINDS = ["bilateral", "call", "mission_meeting", "steering_committee"] as const;
export type MeetingKind = (typeof MEETING_KINDS)[number];
export const MEETING_KIND_LABELS: Labels<typeof MEETING_KINDS> = {
  bilateral: "Bilateral meeting",
  call: "Call",
  mission_meeting: "Mission meeting",
  steering_committee: "Steering committee",
};
export const MEETING_MODES = ["in_person", "online", "hybrid"] as const;
export type MeetingMode = (typeof MEETING_MODES)[number];
export const MEETING_MODE_LABELS: Labels<typeof MEETING_MODES> = { in_person: "In person", online: "Online", hybrid: "Hybrid" };

export const PAPER_SOURCES = ["none", "report_progress", "amendment", "cv_comparison", "calendar", "upload"] as const;
export type PaperSource = (typeof PAPER_SOURCES)[number];

export const REGISTRATION_ROLES = ["participant", "speaker", "host", "moderator"] as const;
export type RegistrationRole = (typeof REGISTRATION_ROLES)[number];
export const REGISTRATION_ROLE_LABELS: Labels<typeof REGISTRATION_ROLES> = {
  participant: "Participant",
  speaker: "Speaker",
  host: "Host",
  moderator: "Moderator",
};

/** How an event takes place (Edit event): drives "How will you join?", the Joining column, online check-in and the link in emails. */
export const EVENT_FORMATS = ["in_person", "online", "hybrid"] as const;
export type EventFormat = (typeof EVENT_FORMATS)[number];
export const EVENT_FORMAT_LABELS: Labels<typeof EVENT_FORMATS> = { in_person: "In person", online: "Online", hybrid: "Hybrid" };
/** How a guest joins a hybrid event (their RSVP answer). */
export const JOINING_MODES = ["in_person", "online"] as const;
export type JoiningMode = (typeof JOINING_MODES)[number];
export const JOINING_MODE_LABELS: Labels<typeof JOINING_MODES> = { in_person: "In person", online: "Online" };
/** The reminder with the programme goes out this many days before the event (GuestReceives.reminder5Days). */
export const GUEST_REMINDER_DAYS = 5;
/** Gender balance (Edit event): the default upper limit for any one gender, in percent. */
export const GENDER_BALANCE_DEFAULT_MAX_PCT = 60;

/** E1: the kinds of block in an event programme. */
export const PROGRAMME_BLOCK_TYPES = ["session", "break", "meal", "registration", "networking", "other"] as const;
export type ProgrammeBlockType = (typeof PROGRAMME_BLOCK_TYPES)[number];
export const PROGRAMME_BLOCK_TYPE_LABELS: Labels<typeof PROGRAMME_BLOCK_TYPES> = {
  session: "Session",
  break: "Coffee break",
  /** E25 (retest 30 Oct): the kind; the block itself is still named by its time (EV9). */
  meal: "Lunch/dinner",
  registration: "Registration",
  networking: "Networking",
  other: "Other",
};
/** E14 (retest 30 Oct, wireframe): when an event to-do happens; null = no phase. */
export const TODO_PHASES = ["before_tor", "preparation", "event_week", "on_the_day", "after"] as const;
export type TodoPhase = (typeof TODO_PHASES)[number];
export const TODO_PHASE_LABELS: Labels<typeof TODO_PHASES> = {
  before_tor: "Before the ToR",
  preparation: "Preparation",
  event_week: "Week of the event",
  on_the_day: "On the day",
  after: "After the event",
};
/** EV9: the title a meal gets from its time ("Breakfast", "Lunch", "Dinner"); "" without a valid time. */
export function mealTitleOf(time: string): string {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return "";
  if (time < "10:30") return "Breakfast";
  if (time < "16:00") return "Lunch";
  return "Dinner";
}
/** EV9: meal titles nobody typed (the old "Meal" and "Lunch/dinner" too): they follow the block's time. */
export const AUTO_MEAL_TITLES: ReadonlySet<string> = new Set(["", "Meal", "Breakfast", "Lunch", "Dinner", "Lunch/dinner"]);
/** EV9: a meal's title by its time while nobody typed one; any other block keeps its title. */
export const programmeBlockTitle = (b: { blockType: string; title: string; startTime: string }): string =>
  b.blockType === "meal" && AUTO_MEAL_TITLES.has(b.title.trim()) ? mealTitleOf(b.startTime) || b.title : b.title;
/** E1: the roles on a programme block; the same values as the guest roles Speaker and Moderator. */
export const SESSION_PERSON_ROLES = ["speaker", "moderator"] as const satisfies readonly RegistrationRole[];
export type SessionPersonRole = (typeof SESSION_PERSON_ROLES)[number];
export const SESSION_PERSON_ROLE_LABELS: Labels<typeof SESSION_PERSON_ROLES> = { speaker: "Speaker", moderator: "Moderator" };
/** E1: whether a speaker or moderator was asked and said yes (set by hand; they are usually invited by email). */
export const SESSION_INVITATION_STATUSES = ["not_invited", "invited", "confirmed", "declined"] as const;
export type SessionInvitationStatus = (typeof SESSION_INVITATION_STATUSES)[number];
export const SESSION_INVITATION_STATUS_LABELS: Labels<typeof SESSION_INVITATION_STATUSES> = {
  not_invited: "Not invited",
  invited: "Invited",
  confirmed: "Confirmed",
  declined: "Declined",
};

export const INVITATION_STATUSES = ["not_sent", "queued", "sent", "opened", "responded"] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];
export const INVITATION_STATUS_LABELS: Labels<typeof INVITATION_STATUSES> = {
  not_sent: "Not sent",
  queued: "Queued",
  sent: "Sent",
  opened: "Opened",
  responded: "Responded",
};

/** v3 question kinds (RSVP and feedback forms). The v2 QUESTION_KINDS above stay for the legacy forms. */
export const EVENT_QUESTION_KINDS = ["short_text", "long_text", "single_choice", "multi_choice", "scale_5", "yes_no", "attendance_days", "stars_5", "scale_10", "ranking", "grid"] as const;
export type EventQuestionKind = (typeof EVENT_QUESTION_KINDS)[number];
export const EVENT_QUESTION_KIND_LABELS: Labels<typeof EVENT_QUESTION_KINDS> = {
  short_text: "Short text",
  long_text: "Long text",
  single_choice: "One choice",
  multi_choice: "Several choices",
  scale_5: "Scale 1 to 5",
  yes_no: "Yes or no",
  attendance_days: "Which days",
  stars_5: "Stars",
  scale_10: "0 to 10",
  ranking: "Ranking",
  grid: "Grid",
};
/**
 * E11 (event wireframes): the kinds only the feedback form asks. Stars (1 to 5),
 * 0 to 10, Ranking (every option in order; the answer is the options, first =
 * rank 1) and Grid (rows × shared columns; the answer is { row: column }).
 * The RSVP form keeps its own kinds.
 */
export const FEEDBACK_ONLY_QUESTION_KINDS = ["stars_5", "scale_10", "ranking", "grid"] as const satisfies readonly EventQuestionKind[];
export const isFeedbackOnlyQuestionKind = (k: string): boolean => (FEEDBACK_ONLY_QUESTION_KINDS as readonly string[]).includes(k);
/** R3: the answer types a share can count (text answers cannot be counted). */
export const COUNTABLE_QUESTION_KINDS = ["single_choice", "multi_choice", "scale_5", "yes_no"] as const satisfies readonly EventQuestionKind[];
export const isCountableQuestionKind = (k: string): k is (typeof COUNTABLE_QUESTION_KINDS)[number] => (COUNTABLE_QUESTION_KINDS as readonly string[]).includes(k);
/**
 * The answers that count as "yes" for a share, as stored on the question:
 * scale "1"…"5", yes/no "yes" or "no", a choice by its option text.
 * Scale and yes/no have a default; a choice question names its own.
 */
export const DEFAULT_COUNTS_AS: Partial<Record<EventQuestionKind, string[]>> = { scale_5: ["4", "5"], yes_no: ["yes"] };
/** The answers a question of this kind can count as "yes" (a choice: its options). */
export const countableAnswers = (kind: string, options: readonly string[]): string[] =>
  kind === "scale_5" ? ["1", "2", "3", "4", "5"] : kind === "yes_no" ? ["yes", "no"] : kind === "single_choice" || kind === "multi_choice" ? [...options] : [];

/**
 * W3: the kinds of proof a line of work asks each of its activities to
 * produce; a new activity copies the list (activity.required_proof). Each
 * kind is also the role of an uploaded evidence file, so "on file" is a file
 * linked to the activity with that role (attendance and feedback also come
 * from check-in and the feedback form; a report also from a submitted
 * mission report or an approved deliverable).
 */
export const PROOF_KINDS = ["attendance", "agenda", "photos", "minutes", "presentations", "feedback", "report", "visibility", "mission_report", "timesheet"] as const;
export type ProofKind = (typeof PROOF_KINDS)[number];
export const PROOF_KIND_LABELS: Labels<typeof PROOF_KINDS> = {
  attendance: "Attendance list",
  agenda: "Agenda",
  photos: "Photos",
  minutes: "Minutes",
  presentations: "Presentations",
  feedback: "Feedback results",
  report: "Report",
  visibility: "Visibility proof",
  mission_report: "Mission report",
  timesheet: "Timesheet",
};
/**
 * AD3 (Marius round 5): the proof that fits each kind of activity. An (N)KE
 * mission is proven by its mission report and the expert's timesheet, not by
 * an attendance list and photos.
 */
export const PROOF_KINDS_FOR: Record<ActivityType, readonly ProofKind[]> = {
  event: ["attendance", "agenda", "photos", "minutes", "presentations", "feedback", "report", "visibility"],
  deliverable: ["report", "visibility", "presentations", "photos"],
  nke: ["mission_report", "timesheet", "minutes", "photos", "visibility"],
};
/** AD3: what a mission must produce when no line of work says otherwise. */
export const NKE_DEFAULT_PROOF: readonly ProofKind[] = ["mission_report", "timesheet"];

export const EXPERT_KINDS = ["ke", "nke"] as const;
export type ExpertKind = (typeof EXPERT_KINDS)[number];
export const EXPERT_KIND_LABELS: Labels<typeof EXPERT_KINDS> = { ke: "Key expert", nke: "Non-key expert" };
export const EXPERT_LEVELS = ["senior", "junior"] as const;
export type ExpertLevel = (typeof EXPERT_LEVELS)[number];
export const EXPERT_LEVEL_LABELS: Labels<typeof EXPERT_LEVELS> = { senior: "Senior", junior: "Junior" };

export const ENGAGEMENT_STATUSES = ["in_pool", "shortlisted", "contracted", "ended"] as const;
export type EngagementStatus = (typeof ENGAGEMENT_STATUSES)[number];
export const ENGAGEMENT_STATUS_LABELS: Labels<typeof ENGAGEMENT_STATUSES> = {
  in_pool: "In the pool",
  shortlisted: "Shortlisted",
  contracted: "Contracted",
  ended: "Ended",
};

export const ASSIGNMENT_ROLES = ["lead", "speaker", "trainer", "author", "reviewer"] as const;
export type AssignmentRole = (typeof ASSIGNMENT_ROLES)[number];
export const ASSIGNMENT_ROLE_LABELS: Labels<typeof ASSIGNMENT_ROLES> = {
  lead: "Lead",
  speaker: "Speaker",
  trainer: "Trainer",
  author: "Author",
  reviewer: "Reviewer",
};

export const TIMESHEET_STATUSES = ["draft", "sent", "question", "signed", "approved"] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];
export const TIMESHEET_STATUS_LABELS: Labels<typeof TIMESHEET_STATUSES> = {
  draft: "Draft",
  sent: "Waiting for signature",
  question: "Sent back with a question",
  signed: "Signed",
  approved: "Approved",
};

export const WORK_PLACES = ["home", "mission", "online"] as const;
export type WorkPlace = (typeof WORK_PLACES)[number];
export const WORK_PLACE_LABELS: Labels<typeof WORK_PLACES> = { home: "Home-based", mission: "On mission", online: "Online" };

/** Selection steps (SPEC-v3 6.4): Profile, Shortlist, Check, Declaration, Contract. */
export const NEED_STATUSES = ["profile", "shortlist", "check", "declaration", "contract", "filled", "cancelled"] as const;
export type NeedStatus = (typeof NEED_STATUSES)[number];
export const NEED_STATUS_LABELS: Labels<typeof NEED_STATUSES> = {
  profile: "Profile",
  shortlist: "Shortlist",
  check: "Checking CVs",
  declaration: "Declaration",
  contract: "Contract",
  filled: "Filled",
  cancelled: "Cancelled",
};
export const CANDIDATE_SOURCES = ["pool", "cv_import", "partner", "call"] as const;
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];
export const CANDIDATE_SOURCE_LABELS: Labels<typeof CANDIDATE_SOURCES> = {
  pool: "Expert pool",
  cv_import: "Imported CV",
  partner: "Proposed by a partner",
  call: "Call for experts",
};
export const CANDIDATE_STATUSES = ["shortlisted", "rejected", "selected"] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const EXPERT_DOC_TYPES = ["declaration", "service_agreement", "confidentiality", "addendum", "proof_of_qualifications", "other"] as const;
export type ExpertDocType = (typeof EXPERT_DOC_TYPES)[number];
export const EXPERT_DOC_TYPE_LABELS: Labels<typeof EXPERT_DOC_TYPES> = {
  declaration: "Declaration of availability and exclusivity",
  service_agreement: "Service agreement",
  confidentiality: "Confidentiality agreement",
  addendum: "Addendum",
  proof_of_qualifications: "Proof of qualifications",
  other: "Other document",
};
export const EXPERT_DOC_STATUSES = ["draft", "to_sign", "signed", "void"] as const;
export type ExpertDocStatus = (typeof EXPERT_DOC_STATUSES)[number];

export const EXPERT_INVOICE_STATUSES = ["submitted", "approved", "partly_approved", "sent_back", "paid"] as const;
export type ExpertInvoiceStatus = (typeof EXPERT_INVOICE_STATUSES)[number];
export const EXPERT_INVOICE_STATUS_LABELS: Labels<typeof EXPERT_INVOICE_STATUSES> = {
  submitted: "Submitted",
  approved: "Approved",
  partly_approved: "Partly approved",
  sent_back: "Sent back",
  paid: "Paid",
};

export const SECTORS = ["government", "civil_society", "academia", "media", "international", "private", "eu_institution"] as const;
export type Sector = (typeof SECTORS)[number];
export const SECTOR_LABELS: Labels<typeof SECTORS> = {
  government: "Government",
  civil_society: "Civil society",
  academia: "Academia",
  media: "Media",
  international: "International organisation",
  private: "Private sector",
  eu_institution: "EU institution",
};

export const CONSENT_BASES = ["consent", "legitimate_interest"] as const;
export type ConsentBasis = (typeof CONSENT_BASES)[number];
export const CONSENT_SOURCES = ["rsvp", "walk_in", "manual", "import", "upload", "renewal"] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];
export const CONSENT_SOURCE_LABELS: Labels<typeof CONSENT_SOURCES> = {
  rsvp: "Gave consent on the RSVP form",
  walk_in: "Gave consent at check-in",
  manual: "Recorded by the team",
  import: "Imported",
  upload: "Signed on paper",
  renewal: "Renewed by email",
};

export const REQUEST_KINDS = ["access", "deletion", "correction"] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];
export const REQUEST_KIND_LABELS: Labels<typeof REQUEST_KINDS> = {
  access: "A copy of their data",
  deletion: "Deletion",
  correction: "Correction",
};
export const REQUEST_STEPS = ["received", "identity_checked", "prepared", "sent", "erased", "done"] as const;
export type RequestStep = (typeof REQUEST_STEPS)[number];

/**
 * The kinds of report to the funder. A grant reports interim per period and finally; a service
 * contract (onboarding Task 2.6, board A4f) an inception report, progress reports and the final
 * report, and when it is fee-based the expenditure verification with the final report.
 */
export const REPORT_KINDS = ["interim", "final", "inception", "progress", "expenditure_verification"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];
export const REPORT_KIND_LABELS: Labels<typeof REPORT_KINDS> = {
  interim: "Interim report",
  final: "Final report",
  inception: "Inception report",
  progress: "Progress report",
  expenditure_verification: "Expenditure verification",
};
/** The kinds a calendar has several of, numbered in their order ("Progress report 2"); the others stand alone. */
const NUMBERED_REPORT_KINDS: readonly string[] = ["interim", "progress"];
/** "Interim report 2", "Progress report 1", "Inception report", "Final report", "Expenditure verification". */
export function reportTitle(kind: string, number: number | null | undefined): string {
  const label = (REPORT_KIND_LABELS as Record<string, string>)[kind] ?? REPORT_KIND_LABELS.interim;
  return NUMBERED_REPORT_KINDS.includes(kind) && number ? `${label} ${number}` : label;
}
/** A report on the whole project (the final report, the expenditure verification), not on one period of it. */
export const isWholeProjectReport = (kind: string): boolean => kind === "final" || kind === "expenditure_verification";
/** A report an interim payment goes with: an interim report, or a service contract's progress report (Task 2.6). */
export const isInterimPaymentReport = (kind: string): boolean => kind === "interim" || kind === "progress";
/**
 * The numbers of a calendar's reports, given in due-date order: each numbered kind counts on its
 * own (interim 1, 2…; progress 1, 2…), the final report takes the number of reports (as it always
 * has), the others 1.
 */
export function reportNumbers(sorted: readonly { kind: string }[]): number[] {
  const seen = new Map<string, number>();
  return sorted.map((r) => {
    if (r.kind === "final") return sorted.length;
    const n = (seen.get(r.kind) ?? 0) + 1;
    seen.set(r.kind, n);
    return NUMBERED_REPORT_KINDS.includes(r.kind) ? n : 1;
  });
}

export const EXPENSE_KINDS = ["invoice", "claim", "other", "fee"] as const;
export type ExpenseKind = (typeof EXPENSE_KINDS)[number];
export const EXPENSE_KIND_LABELS: Labels<typeof EXPENSE_KINDS> = {
  invoice: "Invoice",
  claim: "Expense claim",
  other: "Other cost",
  fee: "Expert fees",
};
export const EXPENSE_STATUSES = ["to_approve", "approved", "sent_back", "paid", "from_timesheets"] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];
export const EXPENSE_STATUS_LABELS: Labels<typeof EXPENSE_STATUSES> = {
  to_approve: "To approve",
  approved: "Approved",
  sent_back: "Sent back",
  paid: "Paid",
  from_timesheets: "From timesheets",
};
export const CLAIMANT_TYPES = ["expert", "staff", "partner", "supplier"] as const;
export type ClaimantType = (typeof CLAIMANT_TYPES)[number];

export const INVOICE_KINDS = ["prefinancing", "interim", "final", "other"] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
export const INVOICE_KIND_LABELS: Labels<typeof INVOICE_KINDS> = {
  prefinancing: "Pre-financing",
  interim: "Interim payment",
  final: "Final payment",
  other: "Other",
};
/** Payment requests to the funder (table invoice): draft → waiting (sent) → paid (received) or rejected. */
export const INVOICE_STATUSES = ["draft", "waiting", "paid", "rejected"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const AMENDMENT_KINDS = ["budget", "dates", "experts", "activities"] as const;
export type AmendmentKind = (typeof AMENDMENT_KINDS)[number];
export const AMENDMENT_KIND_LABELS: Labels<typeof AMENDMENT_KINDS> = {
  budget: "Budget",
  dates: "Dates",
  experts: "Experts",
  activities: "Activities",
};
export const AMENDMENT_STATUSES = ["draft", "sent", "approved", "approved_with_conditions", "refused", "signed"] as const;
export type AmendmentStatus = (typeof AMENDMENT_STATUSES)[number];
export const AMENDMENT_STATUS_LABELS: Labels<typeof AMENDMENT_STATUSES> = {
  draft: "Draft",
  sent: "With the funder",
  approved: "Approved",
  approved_with_conditions: "Approved with conditions",
  refused: "Refused",
  signed: "Signed",
};
export const AMENDMENT_VIAS = ["addendum", "notification_letter"] as const;
export type AmendmentVia = (typeof AMENDMENT_VIAS)[number];

export const INDICATOR_STATUSES = ["on_track", "behind", "not_confirmed"] as const;
export type IndicatorStatus = (typeof INDICATOR_STATUSES)[number];
export const INDICATOR_STATUS_LABELS: Labels<typeof INDICATOR_STATUSES> = {
  on_track: "On track",
  behind: "Behind",
  not_confirmed: "Not confirmed",
};

export const TOR_FIELD_KINDS = ["short_text", "long_text", "list", "experts"] as const;
export type TorFieldKind = (typeof TOR_FIELD_KINDS)[number];

export const SHARE_AUDIENCES = ["funder", "auditor"] as const;
export type ShareAudience = (typeof SHARE_AUDIENCES)[number];

export const SSO_CONNECTION_TYPES = ["oidc_entra", "oidc_google", "saml"] as const;
export type SsoConnectionType = (typeof SSO_CONNECTION_TYPES)[number];

/**
 * Sign-in methods a deployment can offer (AUTH_METHODS, docs/v3/AUTH-v4.md 2):
 * entra = Microsoft Entra External ID (Convenor's own sign-in tenant),
 * microsoft = customers' Microsoft 365 work accounts, google, and sso =
 * per-org company sign-in (OIDC or SAML on a DNS-proven domain).
 */
export const AUTH_METHODS = ["entra", "password", "magic-link", "email-otp", "google", "microsoft", "sso"] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

/** "privacy": a step of a data request, consent renewals, a withdrawal by the team (ST7). */
export const PERSONAL_DATA_ACTIONS = ["view", "export", "delete", "share_view", "privacy"] as const;
export type PersonalDataAction = (typeof PERSONAL_DATA_ACTIONS)[number];

/** The visual tone of a status chip (STYLE.md). */
export const TONES = ["neutral", "info", "warning", "success", "danger"] as const;
export type Tone = (typeof TONES)[number];

/** Where a customer's organisation runs: the shared environment, or its own (enterprise). Set by staff. */
export const ORG_TIERS = ["standard", "enterprise"] as const;
export type OrgTier = (typeof ORG_TIERS)[number];
